import {
  Alert,
  Form,
  Tag,
} from "antd";
import { Fragment, useEffect, useRef, useState } from "react";
import { getCookie } from '../../../../services/cookies';
import debounce from "lodash/debounce";
import { handleProcedureCalculation } from '../utils/procedureCalculators';
import InputRenderers from "../utils/InputRenderers";
import { calculateFormLayoutSpans, evaluateVisibility, isDependentField, buildDependentLookup } from "../utils/formLayouts";
import LaboratoryCategoryCollapsible from '../LaboratoryCategoryCollapsible';
import { evaluateRanges, getRangeBands } from '../utils/rangeEvaluation';

const rangeEvaluationTag = (evaluation) => {
  if (!evaluation) return null;
  if (evaluation?.isNormal === true) {
    return <Tag className="range-evaluation-tag" color="green">Normal</Tag>;
  }
  if (evaluation.isNormal === false) {
    return (
      <Tag className="range-evaluation-tag" color="orange">
        {evaluation.label ? `Alterado · ${evaluation.label}` : 'Alterado'}
      </Tag>
    );
  }
  return evaluation.label ? <Tag className="range-evaluation-tag">{evaluation.label}</Tag> : null;
};

const LaboratoryForm = ({
  inputs,
  toDoctor,
  disableForm,
  readOnly = false,
  roles,
  status,
  form,
  patient,
  countrySettings,
  vigentExamResultIds,
  fromExternal,
  allExams,
  laboratoryCategoryInfo,
}) => {
  const countryCode = getCookie('country_code');
  const user = getCookie('user');
  const [calculatedFields, setCalculatedFields] = useState(new Set());
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const [typingItems, setTypingItems] = useState(new Set());
  const typingTimersRef = useRef({});

  const markItemTyping = (itemId) => {
    setTypingItems((previous) => {
      if (previous.has(itemId)) return previous;
      const next = new Set(previous);
      next.add(itemId);
      return next;
    });
    clearTimeout(typingTimersRef.current[itemId]);
    typingTimersRef.current[itemId] = setTimeout(() => clearItemTyping(itemId), 700);
  };

  const clearItemTyping = (itemId) => {
    clearTimeout(typingTimersRef.current[itemId]);
    setTypingItems((previous) => {
      if (!previous.has(itemId)) return previous;
      const next = new Set(previous);
      next.delete(itemId);
      return next;
    });
  };

  useEffect(() => {
    const timers = typingTimersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const isDoctor = roles.includes('doctor') || roles.includes('direccion_medica');
  const isPsychologist = roles.includes('equipo_psicologia');

  const handleInputChange = () => {};

  const handleSelectInputChange = () => {};

  const handleTextInputChange = () => {};

  const handleCalculationAndDisable = async (item, value) => {
    const calculationResults = await handleProcedureCalculation(
      item.code,
      value,
      inputs.admission_patient_id,
      form,
      inputs.procedures,
      user
    );
    if (!calculationResults) {
      return;
    }
    const newCalculatedIds = calculationResults
      .map(result => result?.calculatedFieldId)
      .filter(Boolean);

    if (newCalculatedIds.length > 0) {
      setCalculatedFields(previousFields => {
        return new Set([...previousFields, ...newCalculatedIds]);
      });
    }
  };

  const debouncedCalculation = debounce(async (item, value) => {
    setLoadingCalculation(true);
    try {
      await handleCalculationAndDisable(item, value);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCalculation(false);
    }
  }, 500);

  const getOrderedInputTypes = () => {
    const defaultOrder = ['measurements', 'procedures', 'surveys'];
    const order = inputs?.extra_data_exam?.input_types_order || defaultOrder;

    let orderedTypes = [];

    order.forEach(typeKey => {
      if (typeKey === 'measurements') {
        orderedTypes.push({ input_name: 'measurements' });
      } else if (typeKey === 'procedures') {
        orderedTypes.push({ input_name: 'procedures' });
      } else if (typeKey === 'surveys') {
        if (inputs.surveys) {
          orderedTypes.push(...inputs.surveys.map(survey => ({ ...survey, input_name: 'questions' })));
        }
      }
    });

    return orderedTypes;
  };

  const statusText = (item) => {
    let text = '';
    if (item?.result_type?.includes('altered') && item.criterion) {
      text = ` - Alterado (${item.criterion})`
    }
    if (item?.result_type?.includes('reproves')) {
      text = `${text} | Reprobatorio empresa`
    }
    return (<div className="custom_altered">{text}</div>)
  }

  const isCollapsed = laboratoryCategoryInfo?.isCollapsed;

  return (
    <>
      {laboratoryCategoryInfo?.isFirstInCategory && (
        <LaboratoryCategoryCollapsible
          category={laboratoryCategoryInfo.category}
          config={laboratoryCategoryInfo.config}
          isCollapsed={isCollapsed}
          onToggleCollapse={laboratoryCategoryInfo.onToggleCollapse}
        />
      )}
      <div
        className={`laboratory-form-container ${laboratoryCategoryInfo?.isLastInCategory ? '' : 'no-margin-bottom'}`}
        style={isCollapsed ? { display: 'none' } : undefined}
      >
        {laboratoryCategoryInfo?.showGroupSeparator && (
          <div className="laboratory-form-subsection-header" />
        )}
        {laboratoryCategoryInfo?.showFieldGroupSeparator && (
          <div className="laboratory-form-field-group-separator" />
        )}
        {laboratoryCategoryInfo?.examGroupName && (
          <div className="laboratory-form-subsection-header">
            <span>{laboratoryCategoryInfo.examGroupName}</span>
          </div>
        )}
        {getOrderedInputTypes().map((type, typeIndex) => {
          const rawInputs = type.input_name === 'questions'
            ? inputs.questions.filter(question => question.survey_id === type.id)
            : inputs[type.input_name];

          if (!rawInputs || rawInputs.length === 0) return null;

          const itemsWithLayout = calculateFormLayoutSpans(rawInputs, inputs?.form_layout, fromExternal, allExams?.map(exam => exam?.inputs?.form_layout));
          const checkVisible = (item) => {
            return (!item.blocked && !item.medical_visibility) || (!item.blocked && isPsychologist) || (item.medical_visibility && isDoctor);
          };
          if (!itemsWithLayout.some(checkVisible)) return null;
          const dependentLookup = buildDependentLookup(inputs?.form_layout);

          return (
            <Fragment key={typeIndex}>
            {type.detail_description && (
              <div className="mb-20">
                <Alert message={<div dangerouslySetInnerHTML={{ __html: type.detail_description }} />} type="info" />
              </div>
            )}

            <div className="laboratory-form-rows">
              {itemsWithLayout.sort((first, second) => first.order - second.order).map((item, itemIndex, sortedItems) => {
                const isCalculatedField = calculatedFields.has(item.id);
                const disableCondition = readOnly || !((isDoctor && toDoctor) || (!disableForm && !isDoctor) || (isDoctor && item.medical_visibility) || (!disableForm && isPsychologist));
                const visible = (!item.blocked && !item.medical_visibility) || (!item.blocked && isPsychologist) || (item.medical_visibility && isDoctor);

                const fieldGroupName = item.field_group;
                const fieldGroupId = item.field_group_id;
                const previousFieldGroupId = itemIndex > 0 ? sortedItems[itemIndex - 1].field_group_id : null;
                const isFirstInFieldGroup = fieldGroupId && fieldGroupId !== previousFieldGroupId;
                const isFirstOutsideFieldGroup = !fieldGroupId && previousFieldGroupId;

                return (
                  <Fragment key={`${type.input_name}-${item.id}`}>
                    <Form.Item shouldUpdate noStyle>
                      {() => {
                        const layoutVisible = evaluateVisibility(item, type.input_name, form?.getFieldValue, inputs?.form_layout, patient, rawInputs);
                        if (!visible || !layoutVisible) return null;
                        const isDependent = isDependentField(item, dependentLookup);

                        return (
                          <>
                            {isFirstInFieldGroup && fieldGroupName && (
                              <div className="laboratory-form-field-group-header">
                                <span className="laboratory-form-field-group-dot" />
                                <span>{fieldGroupName}</span>
                              </div>
                            )}
                            {isFirstOutsideFieldGroup && (
                              <div className="laboratory-form-field-group-separator" />
                            )}
                            <div className={`laboratory-form-row ${isDependent ? 'dependent-field' : ''}`}>
                            <div className="laboratory-form-cell-name">
                              <span className="laboratory-form-item-name">
                                <span dangerouslySetInnerHTML={{ __html: item.name }} />
                                {vigentExamResultIds?.has(item.id) && <Tag className="vigent-exam ml-10">Vigente</Tag>}
                                {countrySettings?.StatusDetail?.[countryCode] && statusText(item)}
                              </span>
                            </div>

                            <div
                              className="laboratory-form-cell-result"
                              onChange={() => markItemTyping(item.id)}
                              onBlur={() => clearItemTyping(item.id)}
                            >
                              <Form.Item
                                onChange={handleInputChange}
                                className={`lab-status-overrides ${countryCode} ${status(item, type)}`}
                              >
                                <InputRenderers
                                  item={item}
                                  type={type}
                                  disableCondition={disableCondition}
                                  isCalculatedField={isCalculatedField}
                                  loadingCalculation={loadingCalculation}
                                  handleInputChange={handleInputChange}
                                  handleSelectInputChange={handleSelectInputChange}
                                  handleTextInputChange={handleTextInputChange}
                                  debouncedCalculation={debouncedCalculation}
                                />
                              </Form.Item>
                            </div>
                            <div className="laboratory-form-cell-unit">
                              <span className="laboratory-form-cell-text">
                                {item.unit || '—'}
                              </span>
                            </div>
                            <div className="laboratory-form-cell-reference">
                              <span className="laboratory-form-cell-text">
                                {(() => {
                                  const rangeBands = getRangeBands(item.range);
                                  if (!rangeBands.length) return '—';
                                  const currentResult = form?.getFieldValue([type.input_name, item.id.toString()]) ?? item.result;
                                  const rangeEvaluation = typingItems.has(item.id)
                                    ? null
                                    : evaluateRanges(item.range, currentResult, patient);
                                  return (
                                    <>
                                      {rangeEvaluationTag(rangeEvaluation)}
                                      {rangeBands.map((rangeItem, i) => {
                                        const isMatched = rangeEvaluation?.matchedIndex === i;
                                        const matchedClass = isMatched
                                          ? ` range-item-matched ${rangeEvaluation.isNormal === false ? 'range-item-matched-altered' : 'range-item-matched-normal'}`
                                          : '';
                                        return (
                                          <div key={i} className={`range-item-row${matchedClass}`}>
                                            {rangeItem.label ? (
                                              <>
                                                <strong>{rangeItem.label}:</strong> {rangeItem.display}
                                              </>
                                            ) : (
                                              rangeItem.display
                                            )}
                                          </div>
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                              </span>
                            </div>
                          </div>
                          </>
                        );
                      }}
                    </Form.Item>
                  </Fragment>
                );
              })}
            </div>
          </Fragment>
          );
        })}
      </div>
    </>
  );
};

export default LaboratoryForm;
